/* eslint-env browser */
// About us (owner, 5 Nov 2026: "fix and modernize the about us page as well"), in the
// landing page's look: the night city with the company's statement and three real app
// screens rising from the hero's edge; the owner's own two texts (about_us_content2 and
// about_us_content1 in /languages, still edited in the portal's language editor) beside
// the company's facts from /settings; what we stand for (product facts only, no figures
// we cannot show); a band to get the app or get in touch. The page, the hero and the band
// are the parts Contact us shares (components/landing/SitePage). Words: ab_* keys with
// English defaults, am / om / ti in json/languages-landing.add.json and bundled in
// components/landing/landingWords.js (lp_* words reused where they fit). Customer and
// trip, never rider or ride.
import React from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, Button, Grid } from '@mui/material';
import {
  ArrowForward,
  ReceiptLong,
  Badge,
  Handshake,
  SupportAgent,
  ApartmentRounded,
  PlaceOutlined,
  PhoneOutlined,
  MailOutlineRounded,
  InfoOutlined,
} from '@mui/icons-material';
import PhoneFrame from '../components/landing/PhoneFrame';
import Reveal from '../components/landing/Reveal';
import SitePage, { NightHero, AppBand, useSiteWords, enter, textLinkSx, eyebrowSx, h2Sx } from '../components/landing/SitePage';
import { tokens as c, iconHues } from '../theme';
import iosHome from '../assets/img/landing/ios-home.webp';
import iosRequests from '../assets/img/landing/ios-requests.webp';
import androidAccepted from '../assets/img/landing/android-accepted.webp';

export default function AboutUs(props) {
  const { t, L, track } = useSiteWords();
  const settings = useSelector((state) => state.settingsdata.settings) || {};

  // The owner's texts, in the order the page always showed them; a missing one is left out.
  const story = ['about_us_content2', 'about_us_content1'].map((k) => t(k, { defaultValue: '' })).filter(Boolean);

  // The company as /settings has it (the Contact page's facts); only those that are set.
  const phoneHref = settings.CompanyPhone ? 'tel:' + String(settings.CompanyPhone).replace(/[^\d+]/g, '') : null;
  const facts = [
    settings.CompanyName ? { id: 'company', icon: <ApartmentRounded />, hue: iconHues.accent, label: L('ab_fact_company', 'Company'), value: settings.CompanyName } : null,
    settings.CompanyAddress ? { id: 'address', icon: <PlaceOutlined />, hue: iconHues.info, label: L('ab_fact_address', 'Address'), value: settings.CompanyAddress } : null,
    phoneHref ? { id: 'phone', icon: <PhoneOutlined />, hue: iconHues.success, label: t('phone'), value: settings.CompanyPhone, href: phoneHref } : null,
    settings.contact_email ? { id: 'email', icon: <MailOutlineRounded />, hue: iconHues.violet, label: t('email'), value: settings.contact_email, href: 'mailto:' + settings.contact_email } : null,
  ].filter(Boolean);

  const values = [
    { id: 'prices', icon: <ReceiptLong />, hue: iconHues.amber, title: L('ab_v1_t', 'Clear prices'), desc: L('ab_v1_d', 'Customers see the fare before they book, and drivers see it before they accept.') },
    { id: 'who', icon: <Badge />, hue: iconHues.info, title: L('ab_v2_t', 'Know who is coming'), desc: L('ab_v2_d', "The driver's name, car, plate and how soon they arrive, as soon as they accept.") },
    { id: 'drivers', icon: <Handshake />, hue: iconHues.success, title: L('ab_v3_t', 'Fair to drivers'), desc: L('ab_v3_d', 'A lower commission, and every request shows the fare and the pickup distance first.') },
    { id: 'help', icon: <SupportAgent />, hue: iconHues.violet, title: L('ab_v4_t', 'People to talk to'), desc: L('ab_v4_d', 'Call our team on 9260 when you need help.'), href: 'tel:9260' },
  ];

  return (
    <SitePage mainId="ab-main" {...props}>
      {/* ── Hero: the statement, then three screens rising from the hero's edge ── */}
      <NightHero
        id="ab-hero-title"
        icon={<InfoOutlined />}
        eyebrow={t('about_us')}
        title={L('ab_hero_title', 'Made in Addis Ababa, for everyone who moves through it.')}
        sub={L('ab_hero_sub', 'Donkey Drive brings customers and drivers together across the city, with the fare shown first and help a phone call away.')}
      >
        {/* A customer's driver on the way, the home screen, a driver's requests: cut by
            the hero's bottom edge, as if rising out of it. */}
        <Box sx={{ position: 'relative', zIndex: 1, mt: { xs: 6, md: 8 }, height: { xs: 250, sm: 330, md: 390 }, maxWidth: 760, mx: 'auto', ...enter(240) }}>
          <Box sx={{ position: 'absolute', left: { xs: '3%', sm: '9%' }, top: { xs: 46, md: 70 }, transform: 'rotate(-9deg)', zIndex: 1 }}>
            <PhoneFrame
              kind="android"
              src={androidAccepted}
              alt={L('lp_alt_accepted', 'Your driver on the way: name, car, plate and minutes away')}
              width={{ xs: 'min(130px, 34vw)', sm: 180, md: 205 }}
              loading="eager"
              onDark
              sx={{ filter: 'brightness(0.94)' }}
            />
          </Box>
          <Box sx={{ position: 'absolute', left: '50%', top: 0, transform: 'translateX(-50%)', zIndex: 2 }}>
            <PhoneFrame
              kind="ios"
              src={iosHome}
              alt={L('lp_alt_home', 'The Donkey Drive home screen: "Where to?" and your recent places')}
              width={{ xs: 'min(160px, 42vw)', sm: 215, md: 245 }}
              loading="eager"
              priority
              onDark
            />
          </Box>
          <Box sx={{ position: 'absolute', right: { xs: '3%', sm: '9%' }, top: { xs: 46, md: 70 }, transform: 'rotate(9deg)', zIndex: 1 }}>
            <PhoneFrame
              kind="ios"
              src={iosRequests}
              alt={L('lp_alt_requests', "The driver's requests, highest fare first")}
              width={{ xs: 'min(130px, 34vw)', sm: 180, md: 205 }}
              loading="eager"
              onDark
            />
          </Box>
        </Box>
      </NightHero>

      {/* ── Our story: the owner's words, beside the company's facts ── */}
      <Box component="section" aria-labelledby="ab-story-title" sx={{ py: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 6, md: 10 }} alignItems="flex-start">
            <Grid item xs={12} md={7}>
              <Reveal>
                <Typography sx={eyebrowSx(track)}>{L('ab_story_eyebrow', 'Our story')}</Typography>
                <Typography component="h2" id="ab-story-title" sx={h2Sx(track)}>
                  {L('ab_story_title', 'Built here, for the streets we know.')}
                </Typography>
                {story.map((text, i) => (
                  // dir="auto": an Arabic text in the language packs reads right to left.
                  <Typography key={i} dir="auto" sx={{ mt: i ? 2.5 : 3.5, fontSize: { xs: 17, md: 19 }, lineHeight: 1.7, color: c.INK_2, textWrap: 'pretty' }}>
                    {text}
                  </Typography>
                ))}
              </Reveal>
            </Grid>
            <Grid item xs={12} md={5}>
              <Reveal delay={120}>
                <Box
                  component="aside"
                  aria-labelledby="ab-facts-title"
                  sx={{ p: { xs: 3, md: 4 }, borderRadius: '28px', bgcolor: c.WHITE, border: `1px solid ${c.OUTLINE}`, boxShadow: '0 40px 80px -48px rgba(19, 12, 9, 0.45)' }}
                >
                  <Typography component="h3" id="ab-facts-title" sx={{ fontSize: 20, fontWeight: 700, color: c.INK, lineHeight: 1.3, mb: facts.length ? 2.5 : 1 }}>
                    {L('ab_facts_title', 'Donkey Drive at a glance')}
                  </Typography>
                  {facts.length ? (
                    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gap: 2.25 }}>
                      {facts.map((f) => (
                        <Box component="li" key={f.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <Box aria-hidden sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: f.hue.bg, color: f.hue.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {f.icon}
                          </Box>
                          <Box sx={{ minWidth: 0, pt: '1px' }}>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 500, color: c.TEXT_3, lineHeight: 1.4 }}>{f.label}</Typography>
                            <Typography dir="auto" sx={{ fontSize: 17, fontWeight: 600, color: c.INK, lineHeight: 1.45, overflowWrap: 'anywhere' }}>
                              {f.href ? (
                                <Box component="a" href={f.href} sx={textLinkSx}>
                                  {f.value}
                                </Box>
                              ) : (
                                f.value
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ) : null}
                  <Button
                    component={RouterLink}
                    to="/contact-us"
                    endIcon={<ArrowForward />}
                    sx={{
                      mt: 3,
                      minHeight: 48,
                      px: 2.5,
                      borderRadius: '12px',
                      color: c.ACCENT_TEXT,
                      fontSize: 16,
                      fontWeight: 700,
                      textTransform: 'none',
                      border: `1.5px solid ${c.ACCENT_TEXT}`,
                      '&:hover': { bgcolor: c.ACCENT_TINT, borderColor: c.ACCENT_TEXT },
                      '&:focus-visible': { outline: `3px solid ${c.ACCENT_TEXT}`, outlineOffset: 3 },
                    }}
                  >
                    {t('contact_us')}
                  </Button>
                </Box>
              </Reveal>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── What we stand for: four promises, each true of the app today ── */}
      <Box component="section" aria-labelledby="ab-values-title" sx={{ py: { xs: 10, md: 14 }, bgcolor: c.SCREEN_BG }}>
        <Container maxWidth="lg">
          <Reveal>
            <Box sx={{ textAlign: 'center', maxWidth: 760, mx: 'auto' }}>
              <Typography sx={eyebrowSx(track)}>{L('ab_values_eyebrow', 'What we stand for')}</Typography>
              <Typography component="h2" id="ab-values-title" sx={h2Sx(track)}>
                {L('ab_values_title', 'The promises behind every trip')}
              </Typography>
            </Box>
          </Reveal>
          <Box
            component="ul"
            sx={{ listStyle: 'none', p: 0, m: 0, mt: { xs: 6, md: 8 }, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: { xs: 2.5, md: 3 } }}
          >
            {values.map((v, i) => (
              <Reveal component="li" key={v.id} delay={i * 100}>
                <Box sx={{ height: '100%', boxSizing: 'border-box', p: { xs: 3, md: 3.5 }, borderRadius: '24px', bgcolor: c.WHITE, border: `1px solid ${c.OUTLINE}` }}>
                  <Box aria-hidden sx={{ width: 52, height: 52, borderRadius: '50%', bgcolor: v.hue.bg, color: v.hue.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5 }}>
                    {v.icon}
                  </Box>
                  <Typography component="h3" sx={{ fontSize: 19, fontWeight: 700, color: c.INK, lineHeight: 1.3, textWrap: 'balance' }}>{v.title}</Typography>
                  {v.href ? (
                    <Typography component="a" href={v.href} sx={{ ...textLinkSx, display: 'inline-block', mt: 1, fontSize: 16, lineHeight: 1.55, fontWeight: 500 }}>
                      {v.desc}
                    </Typography>
                  ) : (
                    <Typography sx={{ mt: 1, fontSize: 16, color: c.INK_2, lineHeight: 1.55, textWrap: 'pretty' }}>{v.desc}</Typography>
                  )}
                </Box>
              </Reveal>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── Get the app, or get in touch ── */}
      <AppBand
        id="ab-cta-title"
        title={L('ab_cta_title', 'Get the app, or get in touch')}
        sub={L('ab_cta_sub', 'Donkey Drive is on iPhone and Android. Questions? Our team is glad to help.')}
      >
        <Button
          component={RouterLink}
          to="/contact-us"
          endIcon={<ArrowForward />}
          sx={{
            mt: 2.5,
            width: { xs: '100%', sm: 'auto' },
            minHeight: 48,
            px: 2.5,
            borderRadius: '12px',
            color: c.WHITE,
            fontSize: 16,
            fontWeight: 700,
            textTransform: 'none',
            border: '1.5px solid rgba(255, 255, 255, 0.7)',
            '@media (pointer: coarse)': { minHeight: 48 },
            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.12)', borderColor: c.WHITE },
            '&:focus-visible': { outline: `3px solid ${c.WHITE}`, outlineOffset: 3 },
          }}
        >
          {t('contact_us')}
        </Button>
      </AppBand>
    </SitePage>
  );
}
